import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { useGameState, PHASE } from '../hooks/useGameState';
import { GAME_CONFIGS } from '../config/gameConfigs';
import Grid from './Grid';
import ChickenArena from './ChickenArena';
import { Stats, GameLog } from './Countdown';
import { BonanzaPool } from './BonanzaPool';
import { BonanzaCountdown } from './BonanzaCountdown';

const styles = {
  app: { minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', padding: '0 16px 40px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0 16px', borderBottom: '1px solid #2a2a2a', marginBottom: '24px' },
  logo: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', letterSpacing: '4px', color: '#F0B800', lineHeight: 1 },
  subtitle: { fontFamily: "'DM Mono', monospace", fontSize: '11px', color: '#666', letterSpacing: '2px', marginTop: '2px' },
  network: { fontFamily: "'DM Mono', monospace", fontSize: '11px', color: '#4CAF50', background: 'rgba(76,175,80,0.1)', padding: '3px 8px', borderRadius: '4px', border: '1px solid rgba(76,175,80,0.2)' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  roundBadge: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '13px', letterSpacing: '2px', color: '#F0B800', background: 'rgba(240,184,0,0.08)', padding: '4px 10px', borderRadius: '4px', border: '1px solid rgba(240,184,0,0.2)' },
  main: { display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px', alignItems: 'start' },
  left: { display: 'flex', flexDirection: 'column', gap: '16px' },
  right: { display: 'flex', flexDirection: 'column', gap: '16px' },
  panel: { background: '#111', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '16px' },
  panelTitle: { fontFamily: "'DM Mono', monospace", fontSize: '11px', color: '#666', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' },
  timerBox: { background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: '8px', padding: '12px', textAlign: 'center', marginBottom: '12px' },
  timerLabel: { fontFamily: "'DM Mono', monospace", fontSize: '10px', color: '#cccccc', letterSpacing: '2px', marginBottom: '4px' },
  timerValue: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', letterSpacing: '4px', lineHeight: 1 },
  timerStatus: { fontFamily: "'DM Mono', monospace", fontSize: '10px', color: '#aaa', marginTop: '4px' },
  testBanner: { fontFamily: "'DM Mono', monospace", fontSize: '11px', color: '#f0b800', background: 'rgba(240,184,0,0.08)', border: '1px solid rgba(240,184,0,0.2)', borderRadius: '6px', padding: '6px 12px', textAlign: 'center', marginBottom: '12px' },
  txBanner: { fontFamily: "'DM Mono', monospace", fontSize: '12px', padding: '8px 12px', borderRadius: '6px', textAlign: 'center', marginBottom: '12px' },
};


const getWalletColor = (walletAddress) => {
  if (!walletAddress) return '#888';
  const colors = [
    '#E05A5A', '#5AAAE0', '#4CAF50', '#9B59B6',
    '#E67E22', '#1ABC9C', '#E91E63', '#FF9800',
    '#00BCD4', '#8BC34A', '#F39C12', '#3498DB'
  ];
  let hash = 0;
  for (let i = 0; i < walletAddress.length; i++) {
    hash = walletAddress.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export default function Game({ gameType = 'mega' }) {
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const gameConfig = GAME_CONFIGS[gameType] || GAME_CONFIGS.mega;
  const game = useGameState(publicKey?.toString(), gameConfig);
  const phaseRef = useRef(game.phase);
  useEffect(() => { phaseRef.current = game.phase; }, [game.phase]);
  const location = useLocation();
  const navigate = useNavigate();
  const walletFromState = useRef(null);
  const arenaMusicRef = useRef(null);
  const cluckRef = useRef(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [purchaseHint, setPurchaseHint] = useState(null);
  const [bonanzaPool, setBonanzaPool] = useState(0);
  const [bonanzaStatus, setBonanzaStatus] = useState(null);
  const [showBonanza, setShowBonanza] = useState(false);
  const [dropLeaderboard, setDropLeaderboard] = useState([]);
  const [freeSquarePending, setFreeSquarePending] = useState(false);
  const [freeSquareTimer, setFreeSquareTimer] = useState(60);
  const [redirectBanner, setRedirectBanner] = useState(null); // { kind: 'autoAssigned'|'pendingPick', squareNum? }
  const [droppingsLeaderboard, setDroppingsLeaderboard] = useState([]);
  const [buySquareMode, setBuySquareMode] = useState(false);
  const [buySquaresRemaining, setBuySquaresRemaining] = useState(0);
  const buySquaresRemainingRef = useRef(0);
  const [freeSquaresRemaining, setFreeSquaresRemaining] = useState(0);
  const freeSquaresRemainingRef = useRef(0);
  const squaresEarnedRef = useRef(1);
  const notificationsSentRef = useRef({
    ten: localStorage.getItem('notif_sent_ten') === 'true',
    five: localStorage.getItem('notif_sent_five') === 'true',
    one: localStorage.getItem('notif_sent_one') === 'true',
  });
  const [notifyPermission, setNotifyPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const playCluck = () => {
    try {
      if (!cluckRef.current) {
        cluckRef.current = new Audio('/cluck.mp3');
      }
      cluckRef.current.currentTime = 0;
      cluckRef.current.volume = 0.7;
      cluckRef.current.play().catch(() => {});
    } catch(e) {}
  };

  const requestNotification = async () => {
    if (!('Notification' in window)) {
      alert('Your browser does not support notifications.');
      return;
    }
    const permission = await Notification.requestPermission();
    setNotifyPermission(permission);
    if (permission === 'granted') {
      new Notification('🐔 Chicken Drop Notifications Enabled!', {
        body: 'We will notify you when the next Chicken Drop game is about to start!',
        icon: '/favicon.ico',
      });
    }
  };

  const startArenaMusic = () => {
    if (!arenaMusicRef.current) {
      arenaMusicRef.current = new Audio('/chicken-drop-arena.mp3');
      arenaMusicRef.current.loop = true;
      arenaMusicRef.current.volume = 0;
    }
    arenaMusicRef.current.play().catch(() => {});
    let vol = 0;
    const fade = setInterval(() => {
      if (!arenaMusicRef.current) { clearInterval(fade); return; }
      vol = Math.min(0.5, vol + 0.02);
      arenaMusicRef.current.volume = vol;
      if (vol >= 0.5) clearInterval(fade);
    }, 100);
  };

  const stopArenaMusic = () => {
    if (!arenaMusicRef.current) return;
    let vol = arenaMusicRef.current.volume;
    const fade = setInterval(() => {
      if (!arenaMusicRef.current) { clearInterval(fade); return; }
      vol = Math.max(0, vol - 0.03);
      arenaMusicRef.current.volume = vol;
      if (vol <= 0) {
        clearInterval(fade);
        arenaMusicRef.current.pause();
        arenaMusicRef.current.currentTime = 0;
      }
    }, 100);
  };

  useEffect(() => {
    if (game.phase === 'walking' || game.phase === 'entering') {
      startArenaMusic();
    } else if (game.phase === 'result' || game.phase === 'waiting') {
      stopArenaMusic();
    }
  }, [game.phase]); // eslint-disable-line

  useEffect(() => {
    return () => {
      if (arenaMusicRef.current) {
        arenaMusicRef.current.pause();
        arenaMusicRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (game.phase === 'result' && game.squaresSold > 0) {
      const walletAddr = publicKey?.toString();
      if (walletAddr) {
        setDropLeaderboard(prev => {
          const existing = prev.find(p => p.fullWallet === walletAddr);
          if (existing) {
            return prev.map(p => p.fullWallet === walletAddr
              ? { ...p, squares: p.squares + game.squaresSold }
              : p
            ).sort((a, b) => b.squares - a.squares);
          }
          return [...prev, {
            wallet: walletAddr.slice(0,4) + '...' + walletAddr.slice(-4),
            fullWallet: walletAddr,
            squares: game.squaresSold,
          }].sort((a, b) => b.squares - a.squares);
        });
      }
    }
  }, [game.phase]);

  const prevWalletRef = useRef(null);
  useEffect(() => {
    const current = publicKey?.toBase58() || null;
    const prev = prevWalletRef.current;
    prevWalletRef.current = current;
    if (prev && current && prev !== current
        && game.phase === PHASE.WAITING && game.squaresSold > 0) {
      navigate('/');
    }
  }, [publicKey]);

  useEffect(() => {
    if (location.state?.pickSquare) {
      if (location.state?.wallet) walletFromState.current = location.state.wallet;
      const earned = location.state?.squaresEarned || 1;
      squaresEarnedRef.current = earned;
      setFreeSquaresRemaining(earned);
      freeSquaresRemainingRef.current = earned;
      setFreeSquarePending(true);
      window.history.replaceState({}, document.title);
      return;
    }
    if (location.state?.autoAssigned) {
      setRedirectBanner({ kind: 'autoAssigned', squareNum: location.state.squareNum });
      window.history.replaceState({}, document.title);
    } else if (location.state?.pendingPick) {
      setRedirectBanner({ kind: 'pendingPick' });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!redirectBanner) return;
    const id = setTimeout(() => setRedirectBanner(null), 10000);
    return () => clearTimeout(id);
  }, [redirectBanner]);

  useEffect(() => {
    if (!freeSquarePending) return;
    setFreeSquareTimer(60);
    const countdown = setInterval(() => {
      setFreeSquareTimer(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          const wallet = walletFromState.current || publicKey?.toString();
          const count = freeSquaresRemainingRef.current;
          const available = Array.from({ length: gameConfig.totalSquares }, (_, i) => i + 1).filter(n => !game.squares[n]);
          let pool = [...available];
          for (let i = 0; i < count && pool.length > 0; i++) {
            const idx = Math.floor(Math.random() * pool.length);
            const isBonus = squaresEarnedRef.current === 2 && i === count - 1;
            game.buySquare(pool[idx], wallet, null, null, false, isBonus ? 'bonus' : 'free');
            if (wallet) {
              fetch(`${process.env.REACT_APP_ORACLE_URL || 'http://localhost:3002'}/delegate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  gameId: gameConfig.id,
                  walletAddress: wallet,
                  squareNum: pool[idx],
                  type: isBonus ? 'bonus' : 'free',
                }),
              }).catch(err => console.warn('[delegate] oracle call failed:', err));
            }
            pool.splice(idx, 1);
          }
          freeSquaresRemainingRef.current = 0;
          setFreeSquaresRemaining(0);
          setFreeSquarePending(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, [freeSquarePending]);

  const roundStartSigRef = useRef(null);

  useEffect(() => {
    const oracleUrl = process.env.REACT_APP_ORACLE_URL || 'http://localhost:3002';
    const gameId = gameConfig?.id;
    if (!gameId) return;

    const fetchOraclePool = async () => {
      try {
        const res = await fetch(`${oracleUrl}/status/${gameId}`);
        if (res.ok) {
          const data = await res.json();
          const newSig = data.roundStartSig ?? null;
          if (roundStartSigRef.current === null) {
            roundStartSigRef.current = newSig;
          } else if (newSig && newSig !== roundStartSigRef.current) {
            roundStartSigRef.current = newSig;
            if (data.lastSettled) {
              game.triggerResult(data.lastSettled.winnerSquare, data.lastSettled.winnerWallet, data.lastSettled.lamports);
            } else {
              game.startNewRound();
            }
            return;
          }
          if (typeof data.roundPool === 'number') {
            game.setOraclePool(data.roundPool);
          }
          if (data.squares && phaseRef.current === PHASE.WAITING) {
            const additions = {};
            for (const [sqNum, value] of Object.entries(data.squares)) {
              if (sqNum === 'null' || sqNum === null) continue; // skip null key bug
              const wallet = typeof value === 'string' ? value : value?.wallet;
              if (!wallet) continue;
              const shortAddr = wallet.slice(0, 4) + '...' + wallet.slice(-4);
              additions[sqNum] = {
                owner: shortAddr,
                walletAddress: wallet,
                color: game.walletColor ? game.walletColor(wallet) : '#888888',
              };
            }
            if (Object.keys(additions).length > 0) {
              game.bulkFillSquares(additions);
            }
          }
        }
      } catch (err) {
        // oracle unreachable - fall back to local estimate
      }
    };

    fetchOraclePool();
    const interval = setInterval(fetchOraclePool, 5000);
    return () => clearInterval(interval);
  }, [gameConfig?.id]);

  useEffect(() => {
    const oracleUrl = process.env.REACT_APP_ORACLE_URL || 'http://localhost:3002';
    const fetchBonanzaStatus = async () => {
      try {
        const res = await fetch(`${oracleUrl}/bonanza/status`);
        if (!res.ok) return;
        const data = await res.json();
        setBonanzaStatus(data);
        if (typeof data.poolBalance === 'number') {
          setBonanzaPool(data.poolBalance);
        }
        if (data.triggered && !showBonanza) {
          const audioUnlock = new Audio('/Bonanza-countdown-music.mp3');
          audioUnlock.volume = 0;
          audioUnlock.play().then(() => {
            audioUnlock.pause();
            audioUnlock.currentTime = 0;
          }).catch(() => {});
          setShowBonanza(true);
        } else if (!data.triggered && showBonanza) {
          setShowBonanza(false);
        }
      } catch (err) {
        // oracle unreachable — keep last known status
      }
    };
    fetchBonanzaStatus();
    const interval = setInterval(fetchBonanzaStatus, 10000);
    return () => clearInterval(interval);
  }, [showBonanza]);

  useEffect(() => {
    if (notifyPermission !== 'granted' || !notificationsEnabled) return;

    const squaresLeft = 100 - game.squaresSold;

    if (squaresLeft <= 10 && squaresLeft > 5 && !notificationsSentRef.current.ten) {
      notificationsSentRef.current.ten = true;
      localStorage.setItem('notif_sent_ten', 'true');
      new Notification('🐔 Chicken Drop Starting Soon!', {
        body: 'Only 10 squares left! Grab yours before the game starts!',
        icon: '/favicon.ico',
        requireInteraction: false,
      });
      playCluck();
    }

    if (squaresLeft <= 5 && squaresLeft > 1 && !notificationsSentRef.current.five) {
      notificationsSentRef.current.five = true;
      localStorage.setItem('notif_sent_five', 'true');
      new Notification('🐔 Chicken Drop Almost Full!', {
        body: 'Only 5 squares remaining! Get in now before its too late!',
        icon: '/favicon.ico',
        requireInteraction: false,
      });
      playCluck();
      setTimeout(() => playCluck(), 300);
    }

    if (squaresLeft <= 1 && !notificationsSentRef.current.one) {
      notificationsSentRef.current.one = true;
      localStorage.setItem('notif_sent_one', 'true');
      new Notification('🚨 LAST SQUARE! Chicken Drop Starting NOW!', {
        body: 'Only 1 square left — the chicken is about to pick a winner! Watch now!',
        icon: '/favicon.ico',
        requireInteraction: true,
      });
      playCluck();
      setTimeout(() => playCluck(), 300);
      setTimeout(() => playCluck(), 600);
    }
  }, [game.squaresSold, notifyPermission]);

  useEffect(() => {
    notificationsSentRef.current = { ten: false, five: false, one: false };
    localStorage.removeItem('notif_sent_ten');
    localStorage.removeItem('notif_sent_five');
    localStorage.removeItem('notif_sent_one');
  }, [game.roundNum]);

  useEffect(() => {
    const loadLeaderboard = () => {
      try {
        const stored = localStorage.getItem('droppingsLeaderboard');
        if (stored) setDroppingsLeaderboard(JSON.parse(stored));
      } catch(e) {}
    };
    loadLeaderboard();
    window.addEventListener('storage', loadLeaderboard);
    window.addEventListener('focus', loadLeaderboard);
    return () => {
      window.removeEventListener('storage', loadLeaderboard);
      window.removeEventListener('focus', loadLeaderboard);
    };
  }, []);

  const handleSquareClick = async (num) => {
    if (game.squares[num]) return;
    if (game.phase !== PHASE.WAITING) return;
    if (!freeSquarePending && !buySquareMode) {
      setPurchaseHint('Want this square? 👇 Purchase below!');
      setTimeout(() => setPurchaseHint(null), 3500);
      return;
    }
    if (game.txStatus === 'pending') return;
    setSelectedSquare(num);
    const wallet = publicKey ? publicKey.toString() : null;
    if (buySquareMode) {
      const isFirstDirectBuy = buySquaresRemainingRef.current === 1;
      const success = await game.buySquare(num, wallet, sendTransaction, connection, isFirstDirectBuy, isFirstDirectBuy ? 'direct' : 'game');
      if (!success && isFirstDirectBuy) {
        setSelectedSquare(null);
        setBuySquareMode(false);
        buySquaresRemainingRef.current = 0;
        setBuySquaresRemaining(0);
        return;
      }
    } else {
      if (freeSquarePending) {
        const isBonus = squaresEarnedRef.current === 2 && freeSquaresRemainingRef.current === 1;
        game.addLog('Registering your square on-chain... you are only charged a < $0.001 network fee!');
        await game.buySquare(num, wallet, sendTransaction, connection, false, isBonus ? 'bonus' : 'free');
      } else {
        await game.buySquare(num, wallet, sendTransaction, connection, true, 'game');
      }
    }
    setSelectedSquare(null);
    if (freeSquarePending) {
      const newRemaining = freeSquaresRemaining - 1;
      setFreeSquaresRemaining(newRemaining);
      freeSquaresRemainingRef.current = newRemaining;
      if (newRemaining <= 0) setFreeSquarePending(false);
    }
    if (buySquareMode) {
      const newRemaining = buySquaresRemainingRef.current - 1;
      buySquaresRemainingRef.current = newRemaining;
      setBuySquaresRemaining(newRemaining);
      game.log.push(`✓ Square purchased for ${gameConfig.directBuyFee} SOL`);
      if (newRemaining <= 0) {
        setBuySquareMode(false);
        buySquaresRemainingRef.current = 0;
      }
    }
  };

  const effectiveWallet = publicKey?.toString() || null;
  const displayWallet = publicKey?.toString() || 'No wallet connected';

  if (process.env.NODE_ENV === 'development') {
    console.log('effectiveWallet:', effectiveWallet);
    console.log('squares sample:', JSON.stringify(Object.entries(game.squares).slice(0, 3)));
  }

  const mySquares = Object.entries(game.squares)
    .filter(([, data]) => {
      const wallet = effectiveWallet;
      if (!wallet) return false;
      return data?.walletAddress === wallet ||
             data?.owner === wallet ||
             data?.fullWallet === wallet ||
             data?.wallet === wallet ||
             (typeof data === 'string' && data === wallet);
    })
    .map(([num]) => Number(num));

  const timerColor = '#4CAF50';
  const txColor = game.txStatus === 'confirmed' ? '#4CAF50' : game.txStatus === 'error' ? '#E05A5A' : '#F0B800';
  const txMsg = game.txStatus === 'pending' ? 'Processing...' : game.txStatus === 'confirmed' ? 'Square purchased!' : game.txStatus === 'error' ? 'Transaction failed' : null;

  return (
    <>
    <style>{`
      ::-webkit-scrollbar { width: 24px; }
      ::-webkit-scrollbar-track { background: #1a1a1a; border-radius: 12px; }
      ::-webkit-scrollbar-thumb { background: #F0B800; border-radius: 12px; border: 3px solid #1a1a1a; }
      ::-webkit-scrollbar-thumb:hover { background: #FFE033; border: 2px solid #1a1a1a; }
      ::-webkit-scrollbar-thumb:active { background: #FFEF80; border: 2px solid #1a1a1a; }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
    `}</style>
    <div style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 10002 }}>
      <WalletMultiButton />
    </div>
    <div style={styles.app}>
      {redirectBanner && (
        <div style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 13,
          padding: '10px 14px',
          margin: '12px 0 0',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          background: redirectBanner.kind === 'autoAssigned' ? 'rgba(240,184,0,0.12)' : 'rgba(76,175,80,0.12)',
          border: `1px solid ${redirectBanner.kind === 'autoAssigned' ? 'rgba(240,184,0,0.4)' : 'rgba(76,175,80,0.4)'}`,
          color: redirectBanner.kind === 'autoAssigned' ? '#F0B800' : '#4CAF50',
        }}>
          <span>
            {redirectBanner.kind === 'autoAssigned'
              ? `🐔 Your square was auto-assigned! Square #${redirectBanner.squareNum} is yours — highlighted below.`
              : '🐔 Welcome back! You paid for a square — pick it before it auto-assigns!'}
          </span>
          <button
            onClick={() => setRedirectBanner(null)}
            style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: 16, cursor: 'pointer', padding: '0 6px', lineHeight: 1 }}
            aria-label="dismiss"
          >×</button>
        </div>
      )}
      <header style={styles.header}>
        <div>
          <div style={styles.logo}>{gameConfig.emoji} {gameConfig.label.toUpperCase()}</div>
          <div style={styles.subtitle}>SOLANA · MULTIPLAYER · PICK YOUR SQUARE</div>
          <div style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: '13px',
            color: '#F0B800',
            fontWeight: 700,
            textAlign: 'center',
            padding: '10px 20px',
            background: 'rgba(240,184,0,0.08)',
            border: '1px solid rgba(240,184,0,0.25)',
            borderRadius: '8px',
            marginTop: '8px',
            letterSpacing: '0.5px',
            lineHeight: 1.6,
          }}>
            {freeSquarePending ? (
              <div>
                <div>Thanks for playing Chicken Droppings!!</div>
                <div style={{ marginTop: 4 }}>You earned 1 free square!</div>
                <div style={{ marginTop: 4 }}>Click the grid below to choose!!</div>
              </div>
            ) : 'Thanks for playing Chicken Droppings!! Now pick your square — if the Chicken drops its golden egg on your number you win the Chicken Drop pool prize!!!'
            }
          </div>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.network}>DEVNET</span>
          {(freeSquarePending || buySquareMode) ? (
            <span style={{
              background: '#555',
              color: '#888',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '1px',
              cursor: 'not-allowed',
              userSelect: 'none',
              display: 'inline-block',
            }}
              title="Please select your square(s) first"
            >
              🏠 HOME - CHICKEN DROPPINGS
            </span>
          ) : (
            <Link to="/" style={{ background: '#c0392b', color: 'white', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', fontSize: '13px', fontWeight: 700, letterSpacing: '1px' }}>
              🏠 HOME - CHICKEN DROPPINGS
            </Link>
          )}
        </div>
      </header>

      <div style={styles.main}>
        <div style={styles.left}>
          <div style={styles.panel}>
            <div style={styles.panelTitle}>Arena</div>
            {(game.phase === 'entering' || game.phase === 'walking') && (
              <div style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontSize: 22,
                color: '#F0B800',
                letterSpacing: 3,
                textAlign: 'center',
                marginBottom: 8,
                textShadow: '0 0 20px rgba(240,184,0,0.6)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}>
                🐔 GOOD LUCK!! YOU WIN IF THE CHICKEN LAYS AN EGG ON YOUR SQUARE!! 🥚
              </div>
            )}
            <ChickenArena
              phase={game.phase}
              walkPath={game.walkPath}
              chickenPos={game.chickenPos}
              winningSquare={game.winningSquare}
              squares={game.squares}
              resultMessage={game.resultMessage}
              resultCountdown={game.resultCountdown}
              publicKey={publicKey?.toString()}
              onSquareClick={handleSquareClick}
              mySquares={mySquares}
              gridSize={gameConfig.gridSize}
              poolTotal={game.displayPool}
              settledPrize={game.settledPrize}
            />
          </div>

          {purchaseHint && (
            <div style={{
              background: 'rgba(240,184,0,0.18)',
              border: '2px solid #F0B800',
              borderRadius: 10,
              padding: '12px 16px',
              marginTop: 10,
              textAlign: 'center',
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 20,
              color: '#F0B800',
              letterSpacing: 2,
            }}>
              {purchaseHint}
            </div>
          )}

          {freeSquarePending && (
            <div style={{ background: 'rgba(240,184,0,0.12)', border: '2px solid #F0B800', borderRadius: 10, padding: '12px 16px', marginTop: 10, textAlign: 'center' }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: '#F0B800', letterSpacing: 2, marginBottom: 4 }}>
                🐔 PICK YOUR {freeSquaresRemaining} FREE SQUARE{freeSquaresRemaining !== 1 ? 'S' : ''}!
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#aaa', marginBottom: 6 }}>
                {freeSquaresRemaining} square{freeSquaresRemaining !== 1 ? 's' : ''} remaining to pick · Auto-assigned in {freeSquareTimer}s
              </div>
              <button onClick={() => { setFreeSquarePending(false); setFreeSquaresRemaining(0); freeSquaresRemainingRef.current = 0; }} style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#555', background: 'none', border: 'none', cursor: 'pointer' }}>
                skip
              </button>
            </div>
          )}

          {!freeSquarePending && !buySquareMode && game.phase === PHASE.WAITING && gameConfig.id !== 'bonanza' && (
            <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 10, padding: '12px 16px', marginTop: 10, textAlign: 'center' }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: '#F0B800', letterSpacing: 1, marginBottom: 6, textAlign: 'center' }}>
                🐔 THE CHICKEN DROP STARTS AUTOMATICALLY ONCE ALL SQUARES ARE FULL
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: '#cccccc', marginBottom: 8 }}>
                Speed up the next Chicken Drop AND WIN!
              </div>
              <button
                onClick={() => {
                  setBuySquareMode(true);
                  setBuySquaresRemaining(1);
                  buySquaresRemainingRef.current = 1;
                }}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, letterSpacing: 2, color: '#000', background: '#F0B800', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer' }}
              >
                {`FILL IN 1 MORE SQUARE FOR ${gameConfig.directBuyFee} SOL`}
              </button>
              {(freeSquarePending || buySquareMode) ? (
                <div style={{
                  marginTop: 10,
                  background: '#555',
                  color: '#888',
                  padding: '10px 20px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: 1,
                  cursor: 'not-allowed',
                  textAlign: 'center',
                  userSelect: 'none',
                }}
                  title="Please select your square(s) first"
                >
                  🐓 Play Chicken Droppings Again!
                </div>
              ) : (
                <Link to="/" style={{
                  display: 'block',
                  marginTop: 10,
                  background: '#c0392b',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: 1,
                  textDecoration: 'none',
                  textAlign: 'center',
                }}>
                  🐓 Play Chicken Droppings Again!
                </Link>
              )}
            </div>
          )}

          {buySquareMode && (
            <div style={{ background: 'rgba(240,184,0,0.12)', border: '2px solid #F0B800', borderRadius: 10, padding: '12px 16px', marginTop: 10, textAlign: 'center' }}>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: '#F0B800', letterSpacing: 2, marginBottom: 4 }}>
                🥚 PICK YOUR SQUARE
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#aaa', marginBottom: 6 }}>
                Click any available square · {gameConfig.directBuyFee} SOL will be charged
              </div>
            </div>
          )}

          {game.phase === PHASE.WAITING && (
            <>
              <div style={styles.timerBox}>
                <div style={styles.timerLabel}>SQUARES FILLED</div>
                <div style={{ ...styles.timerValue, color: timerColor, fontSize: '36px' }}>
                  {game.squaresSold} / {gameConfig.totalSquares}
                </div>
                <div style={styles.timerStatus}>
                  {game.squaresSold === 0
                    ? 'Waiting for players — Free with Chicken Droppings entry'
                    : game.squaresSold < gameConfig.totalSquares
                      ? `${gameConfig.totalSquares - game.squaresSold} squares remaining · game starts when full`
                      : 'All squares filled!'}
                </div>
              </div>
              {game.squaresSold >= gameConfig.totalSquares && (
                <div style={{
                  background: 'rgba(240,184,0,0.12)',
                  border: '2px solid #F0B800',
                  borderRadius: 10,
                  padding: '12px 16px',
                  marginTop: 10,
                  textAlign: 'center',
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: 20,
                  color: '#F0B800',
                  letterSpacing: 2,
                }}>
                  🐔 Chicken Drop commencing shortly!
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: '8px' }}>
            <GameLog entries={game.log} />
          </div>
        </div>

        <div style={styles.right}>
          {/* 1. Current Chicken Drop Pool */}
          <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '19px', color: '#F0B800', letterSpacing: '2px', marginBottom: '4px' }}>
              🐔 CURRENT CHICKEN DROP POOL
            </div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '38px', color: '#F0B800', letterSpacing: '3px' }}>
              {game.displayPool > 0 ? game.displayPool.toFixed(3) : '0.000'} SOL
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: '#888', marginTop: 4 }}>
              {game.squaresSold} / {gameConfig.totalSquares} squares filled · winner takes the entire pool!
            </div>
            {txMsg && (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', padding: '8px 12px', borderRadius: '6px', textAlign: 'center', marginTop: '8px', color: txColor, background: `${txColor}15`, border: `1px solid ${txColor}40` }}>
                {txMsg}
              </div>
            )}
          </div>

          {/* Notification opt-in */}
          <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 10, padding: '12px 16px', marginTop: 8, textAlign: 'center' }}>
            {notifyPermission === 'granted' ? (
              <div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: '#4CAF50', marginBottom: 8 }}>
                  ✓ Notifications enabled! We will alert you when squares are almost full.
                </div>
                <button
                  onClick={() => { setNotifyPermission('default'); setNotificationsEnabled(false); }}
                  style={{ background: 'none', border: '1px solid #444', borderRadius: 6, padding: '4px 12px', fontSize: '10px', color: '#888', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px' }}
                >
                  Turn off notifications
                </button>
              </div>
            ) : notifyPermission === 'denied' ? (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', color: '#E05A5A' }}>
                Notifications blocked. Please enable them in your browser settings.
              </div>
            ) : (
              <div>
                <button
                  onClick={requestNotification}
                  style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: '13px', fontWeight: 700, fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', width: '100%' }}
                >
                  🔔 Notify me when squares are almost full and the Chicken Drop is about to start!
                </button>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', color: '#666', marginTop: 8 }}>
                  Note: Browser notifications are not supported on iOS devices.
                </div>
              </div>
            )}
          </div>

          {game.phase === PHASE.WALKING && (
            <div style={styles.timerBox}>
              <div style={styles.timerLabel}>CHICKEN IS WANDERING</div>
              <div style={{ ...styles.timerValue, color: '#F0B800', fontSize: '28px' }}>
                🐔 Searching...
              </div>
              <div style={styles.timerStatus}>
                {game.squaresSold} squares filled · {game.displayPool.toFixed(3)} SOL prize pool
              </div>
            </div>
          )}

          {game.phase === PHASE.RESULT && (
            <div style={styles.timerBox}>
              <div style={styles.timerLabel}>NEW ROUND IN</div>
              <div style={{ ...styles.timerValue, color: '#F0B800' }}>
                {game.resultCountdown}s
              </div>
            </div>
          )}

          {/* 4. My Squares */}
          <div style={{ background: '#111', border: '2px solid #333', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', color: '#F0B800', letterSpacing: '2px', marginBottom: 10, fontWeight: 700 }}>
              ★ MY SQUARES
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '16px', color: '#ffffff', fontWeight: 700, letterSpacing: '1px', marginBottom: 8 }}>
              {mySquares.length > 0 ? mySquares.sort((a, b) => a - b).join(', ') : 'No squares yet'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginBottom: 8 }}>
              {(() => { const myColor = mySquares.length > 0 ? (game.squares[mySquares[0]]?.color || getWalletColor(effectiveWallet)) : getWalletColor(effectiveWallet); return (
              <div style={{ width: 16, height: 16, borderRadius: 3, background: myColor, border: '2px solid #F0B800', flexShrink: 0 }}></div>
              ); })()}
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#888' }}>your color on the grid</span>
            </div>
            <div style={{ borderTop: '1px solid #222', paddingTop: 8, marginTop: 4 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', color: '#aaa', marginBottom: 4 }}>WALLET</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', color: '#aaa', wordBreak: 'break-all' }}>
                {displayWallet}
              </div>
            </div>
          </div>

          {/* 5. Progressive Bonanza Pool */}
          <BonanzaPool pool={bonanzaPool} />

          {/* 6. Bonanza Leaderboard */}
          <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: 16, marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#F0B800', letterSpacing: 2, marginBottom: 10, textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>
              🏆 TOP 100 — CHICKEN DROPPINGS LEADERBOARD
            </div>
            <div style={{ fontSize: 10, color: '#666', textAlign: 'center', marginBottom: 8, fontFamily: "'DM Mono', monospace" }}>
              Top 100 scores qualify for Bonanza
            </div>
            {publicKey && (() => {
              const shortW = (w) => w ? w.slice(0,4) + '...' + w.slice(-4) : '????';
              const playerShort = shortW(publicKey.toString());
              const playerIdx = droppingsLeaderboard.findIndex(e => e.wallet === playerShort || e.fullWallet === publicKey.toString());
              if (playerIdx === -1) return (
                <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, padding: '5px 8px', marginBottom: 8, fontSize: 10, color: '#888', textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>
                  Not yet ranked — play Chicken Droppings!
                </div>
              );
              const entry = droppingsLeaderboard[playerIdx];
              return (
                <div style={{ background: 'rgba(240,184,0,0.15)', border: '1px solid #F0B800', borderRadius: 6, padding: '5px 8px', marginBottom: 8, fontSize: 11, display: 'flex', justifyContent: 'space-between', fontFamily: "'DM Mono', monospace" }}>
                  <span style={{ color: '#F0B800' }}>#{playerIdx + 1}</span>
                  <span style={{ color: '#aaa', flex: 1, textAlign: 'center' }}>{entry.wallet}</span>
                  <span style={{ color: '#4CAF50' }}>{(entry.score || 0).toLocaleString()}</span>
                </div>
              );
            })()}
            <div style={{ maxHeight: 300, overflowY: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#F0B800 #1a1a1a' }}>
              {(() => {
                const shortW = (w) => w ? w.slice(0,4) + '...' + w.slice(-4) : '????';
                const playerShort = publicKey ? shortW(publicKey.toString()) : null;
                const rows = [];
                for (let i = 0; i < 100; i++) {
                  const entry = droppingsLeaderboard[i];
                  const isMe = entry && publicKey && (entry.wallet === playerShort || entry.fullWallet === publicKey.toString());
                  rows.push(
                    <div key={i} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '3px 6px',
                      background: isMe ? 'rgba(240,184,0,0.1)' : i % 2 === 0 ? '#111' : '#0a0a0a',
                      borderRadius: 3,
                      fontSize: 10,
                      border: isMe ? '1px solid rgba(240,184,0,0.3)' : 'none',
                      fontFamily: "'DM Mono', monospace",
                    }}>
                      <span style={{ color: i < 3 ? '#F0B800' : '#555', minWidth: 24 }}>#{i + 1}</span>
                      {entry ? (
                        <>
                          <span style={{ color: isMe ? '#F0B800' : '#aaa', flex: 1 }}>{entry.wallet}</span>
                          <span style={{ color: '#888', fontSize: 9, marginRight: 6 }}>R{entry.rounds || 0}</span>
                          <span style={{ color: i < 3 ? '#F0B800' : '#bbb' }}>{(entry.score || 0).toLocaleString()}</span>
                        </>
                      ) : (
                        <span style={{ color: '#333', flex: 1, fontStyle: 'italic' }}>no score yet</span>
                      )}
                    </div>
                  );
                }
                return rows;
              })()}
            </div>
          </div>
        </div>
      </div>
      {showBonanza && (
        <BonanzaCountdown
          pool={bonanzaPool}
          bonanzaStatus={bonanzaStatus}
          dropLeaderboard={dropLeaderboard}
          droppingsLeaderboard={droppingsLeaderboard}
          playerWallet={publicKey?.toString()}
          onBonanzaStart={() => {}}
          onBonanzaEnd={() => {
            setShowBonanza(false);
            setBonanzaPool(0);
            setDropLeaderboard([]);
          }}
        />
      )}
    </div>
    </>
  );
}
